import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';

// Add shimmer and pulse animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
  @keyframes progress-pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
  .animate-progress-pulse {
    animation: progress-pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  @keyframes bar-grow {
    0%, 100% {
      transform: scaleX(1);
    }
    50% {
      transform: scaleX(1.02);
    }
  }
  .animate-bar-grow {
    animation: bar-grow 2s ease-in-out infinite;
    transform-origin: left;
  }
  @keyframes download-pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.02);
    }
  }
  .animate-download-pulse {
    animation: download-pulse 2s ease-in-out infinite;
  }
`;

const JOB_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
};

const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_POLLING_TIME = 600000; // 10 minutes

const ExportProgressModal = ({ isOpen, onClose, initialJobs = [] }) => {
  const [jobs, setJobs] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCancelled, setIsCancelled] = useState(false);
  const pollingTimeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  const DSInstance = getDSInstance();

  useEffect(() => {
    if (isOpen && initialJobs.length > 0) {
      // Initialize jobs with pending status
      const formattedJobs = initialJobs.map(job => ({
        ...job,
        status: JOB_STATUS.PENDING,
        downloadUrl: null,
        thumbnailUrl: null,
        error: null,
        childJobs: job.childJobsId ? job.childJobsId.map(childJobId => ({
          jobId: childJobId,
          status: JOB_STATUS.PENDING,
          downloadUrl: null,
          error: null,
        })) : null,
      }));
      setJobs(formattedJobs);
      startTimeRef.current = Date.now();
      startPolling(formattedJobs);
    }

    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [isOpen, initialJobs]);

  const startPolling = (currentJobs) => {
    const poll = async () => {
      // Check if cancelled
      if (isCancelled) {
        return;
      }

      // Check if we've exceeded max polling time
      if (Date.now() - startTimeRef.current > MAX_POLLING_TIME) {
        setHasError(true);
        setErrorMessage('Export timeout. Some projects took too long to process.');
        return;
      }

      try {
        const updatedJobs = await Promise.all(
          currentJobs.map(async (job) => {
            if (job.status === JOB_STATUS.COMPLETED || job.status === JOB_STATUS.ERROR) {
              return job; // Skip already completed or errored jobs
            }

            try {
              // Build query params
              const params = new URLSearchParams();
              if (job?.templateId) params.append('templateId', job?.templateId);
              if (job?.projectId) params.append('projectId', job?.projectId);
              if (job?.format) params.append('format', job?.format);

              const queryString = params.toString();
              const url = `/template-builder/export/jobs/${job.jobId}/status${queryString ? `?${queryString}` : ''}`;

              const response = await DSInstance.get(url);

              if (response.data.success) {
                const { completed, download_url, thumbnail_url } = response.data.data;

                if (completed) {
                  // If job has child jobs, poll them
                  let updatedChildJobs = job.childJobs;
                  if (job.childJobs && job.childJobs.length > 0) {
                    updatedChildJobs = await Promise.all(
                      job.childJobs.map(async (childJob, childIndex) => {
                        if (childJob.status === JOB_STATUS.COMPLETED || childJob.status === JOB_STATUS.ERROR) {
                          return childJob;
                        }

                        try {
                          // Build query params for child job (inherit from parent job)
                          const childParams = new URLSearchParams();

                          if (job?.templateId) childParams.append('templateId', job?.templateId);
                          if (job?.projectId) childParams.append('projectId', job?.projectId);
                          if (job?.format) childParams.append('format', job?.format);
                          if (job?.jobId) childParams.append('parentId', job?.jobId);
                          childParams.append('order', childIndex);

                          const childQueryString = childParams.toString();

                          const childUrl = `/template-builder/export/jobs/${childJob.jobId}/status${childQueryString ? `?${childQueryString}` : ''}`;

                          const childResponse = await DSInstance.get(childUrl);

                          if (childResponse.data.success) {
                            const { completed: childCompleted, download_url: childDownloadUrl } = childResponse.data.data;

                            if (childCompleted) {
                              return {
                                ...childJob,
                                status: JOB_STATUS.COMPLETED,
                                downloadUrl: childDownloadUrl,
                              };
                            } else {
                              return {
                                ...childJob,
                                status: JOB_STATUS.PROCESSING,
                              };
                            }
                          } else {
                            return {
                              ...childJob,
                              status: JOB_STATUS.ERROR,
                              error: 'Failed to check status',
                            };
                          }
                        } catch (error) {
                          console.error(`Error checking child job ${childJob.jobId}:`, error);
                          return {
                            ...childJob,
                            status: JOB_STATUS.ERROR,
                            error: error.message || 'Unknown error',
                          };
                        }
                      })
                    );
                  }

                  return {
                    ...job,
                    status: JOB_STATUS.COMPLETED,
                    downloadUrl: download_url,
                    thumbnailUrl: thumbnail_url,
                    childJobs: updatedChildJobs,
                  };
                } else {
                  return {
                    ...job,
                    status: JOB_STATUS.PROCESSING,
                  };
                }
              } else {
                return {
                  ...job,
                  status: JOB_STATUS.ERROR,
                  error: 'Failed to check status',
                };
              }
            } catch (error) {
              console.error(`Error checking job ${job.jobId}:`, error);
              return {
                ...job,
                status: JOB_STATUS.ERROR,
                error: error.message || 'Unknown error',
              };
            }
          })
        );

        setJobs(updatedJobs);

        // Check if all jobs and child jobs are completed or errored
        const allFinished = updatedJobs.every(job => {
          const jobFinished = job.status === JOB_STATUS.COMPLETED || job.status === JOB_STATUS.ERROR;
          const childJobsFinished = !job.childJobs || job.childJobs.every(
            childJob => childJob.status === JOB_STATUS.COMPLETED || childJob.status === JOB_STATUS.ERROR
          );
          return jobFinished && childJobsFinished;
        });

        if (allFinished) {
          const completedJobs = updatedJobs.filter(job => job.status === JOB_STATUS.COMPLETED);
          const erroredJobs = updatedJobs.filter(job => job.status === JOB_STATUS.ERROR);

          if (erroredJobs.length > 0 && completedJobs.length === 0) {
            setHasError(true);
            setErrorMessage('All exports failed. Please try again.');
          } else if (completedJobs.length > 0) {
            // Start download process
            // await handleDownloadAll(completedJobs);
          }
        } else {
          // Continue polling
          pollingTimeoutRef.current = setTimeout(() => poll(), POLLING_INTERVAL);
        }
      } catch (error) {
        console.error('Polling error:', error);
        setHasError(true);
        setErrorMessage('Error checking export status. Please try again.');
      }
    };

    // Start the first poll
    poll();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDownloadAll = async (completedJobs) => {
    try {
      setIsDownloading(true);

      // Separate jobs with child jobs from jobs without child jobs
      const jobsWithChildJobs = completedJobs.filter(job => job.childJobs && job.childJobs.length > 0);
      const jobsWithoutChildJobs = completedJobs.filter(job => !job.childJobs || job.childJobs.length === 0);

      // Collect all main job download URLs
      const mainJobUrls = jobsWithoutChildJobs
        .filter(job => job.downloadUrl)
        .map(job => ({
          url: job.downloadUrl,
          filename: `${job.projectName || job.templateName || job.projectId}.${job.format}`,
        }));

      // Collect all child job download URLs
      const allChildJobUrls = jobsWithChildJobs.flatMap((job) => {
        const childUrls = job.childJobs
          .filter(childJob => childJob.status === JOB_STATUS.COMPLETED && childJob.downloadUrl)
          .map((childJob, index) => ({
            url: childJob.downloadUrl,
            filename: `${job.templateName || job.projectName || job.projectId} - Page - ${index + 1}.${job.format}`,
          }));

        console.log(`Job ${job.jobId} has ${job.childJobs?.length || 0} child jobs, ${childUrls.length} completed with URLs`);
        return childUrls;
      });

      // Combine all download URLs
      const allDownloadUrls = [...mainJobUrls, ...allChildJobUrls];

      console.log('Total URLs to download:', allDownloadUrls.length, allDownloadUrls);

      // Trigger all downloads
      if (allDownloadUrls.length > 0) {
        await triggerDownload(allDownloadUrls);
        setDownloadUrl(allDownloadUrls[0].url); // Set first URL for manual download button
      }
    } catch (error) {
      console.error('Download error:', error);
      setHasError(true);
      setErrorMessage('Error downloading files. Please try the manual download link below.');
    } finally {
      setIsDownloading(false);
    }
  };

  const triggerDownload = (urlsOrSingle, overridefilename) => {
    return new Promise(async (resolve) => {
      if (Array.isArray(urlsOrSingle)) {

        const links = [];

        for (let i = 0; i < urlsOrSingle.length; i++) {
          const { url, filename } = urlsOrSingle[i];

          try {
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';

            document.body.appendChild(link);
            links.push(link);

            await new Promise(r => setTimeout(r, 50));

            link.click();

            if (i < urlsOrSingle.length - 1) {
              await new Promise(r => setTimeout(r, 400));
            }
          } catch (error) {
            console.error(`[triggerDownload] Error due ${filename} download:`, error);
          }
        }


        setTimeout(() => {
          links.forEach((link, index) => {
            setTimeout(() => {
              try {
                if (document.body.contains(link)) {
                  document.body.removeChild(link);
                }
              } catch (e) {
                console.error('[triggerDownload] Error due link removal:', e);
              }
            }, index * 500);
          });
        }, 2000);

        resolve();
      } else {
        const link = document.createElement('a');
        link.href = urlsOrSingle;
        link.download = overridefilename;
        link.style.display = 'none';

        document.body.appendChild(link);
        await new Promise(r => setTimeout(r, 100));
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          resolve();
        }, 1000);
      }
    });
  };

  const handleRetry = async (job) => {
    try {
      // Reset job status
      setJobs(prevJobs =>
        prevJobs.map(j =>
          j.jobId === job.jobId
            ? { ...j, status: JOB_STATUS.PENDING, error: null }
            : j
        )
      );

      // Restart polling for this specific job
      startTimeRef.current = Date.now();
      const updatedJobs = jobs.map(j =>
        j.jobId === job.jobId
          ? { ...j, status: JOB_STATUS.PENDING, error: null }
          : j
      );
      startPolling(updatedJobs);
    } catch (error) {
      console.error('Retry error:', error);
    }
  };

  const handleManualDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const handlePreview = (thumbnailUrl) => {
    if (thumbnailUrl) {
      window.open(thumbnailUrl, '_blank');
    }
  };

  const handleIndividualDownload = async (job) => {
    if (!job) return;

    // Se o job tem child jobs, baixar todas as URLs dos child jobs
    if (job.childJobs && job.childJobs.length > 0) {
      const childUrls = job.childJobs
        .filter(childJob => childJob.status === JOB_STATUS.COMPLETED && childJob.downloadUrl)
        .map((childJob, index) => ({
          url: childJob.downloadUrl,
          filename: `${job.templateName || job.projectName || job.projectId} - Page - ${index + 1}.${job.format}`,
        }));

      if (childUrls.length > 0) {
        await triggerDownload(childUrls);
      }
    } else {
      // Se não tem child jobs, apenas baixar a URL do job principal
      if (job.downloadUrl) {
        await triggerDownload(job.downloadUrl, `${job.projectName || job.projectId}.${job.format}`);
      }
    }
  };

  const handleCancel = () => {
    setIsCancelled(true);
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    handleClose();
  };

  const handleClose = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    setJobs([]);
    setIsDownloading(false);
    setDownloadUrl(null);
    setHasError(false);
    setErrorMessage('');
    setIsCancelled(false);
    onClose();
  };

  if (!isOpen) return null;

  const completedCount = jobs.filter(j => j.status === JOB_STATUS.COMPLETED).length;
  const errorCount = jobs.filter(j => j.status === JOB_STATUS.ERROR).length;
  const processingCount = jobs.filter(j => j.status === JOB_STATUS.PROCESSING).length;
  const pendingCount = jobs.filter(j => j.status === JOB_STATUS.PENDING).length;
  const totalJobs = jobs.length;
  const allCompleted = completedCount + errorCount === totalJobs;

  // Calculate progress considering processing jobs as 50% complete
  const progress = totalJobs > 0
    ? ((completedCount + (processingCount * 0.5)) / totalJobs) * 100
    : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case JOB_STATUS.COMPLETED:
        return (
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case JOB_STATUS.ERROR:
        return (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case JOB_STATUS.PROCESSING:
        return (
          <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      default: // PENDING
        return (
          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const canClose = allCompleted || hasError;
  const isProcessing = !allCompleted && !hasError;

  return (
    <>
      <style>{shimmerStyles}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-800">
              {allCompleted ? 'Export Complete' : 'Exporting Projects'}
            </h2>
            {canClose && (
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="p-6 overflow-y-auto overflow-x-hidden max-h-[calc(80vh-200px)]">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress: {completedCount}/{totalJobs} projects
              </span>
              <span className="text-sm font-medium text-gray-700">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden relative">
              {/* Background pulse effect when processing */}
              {isProcessing && (
                <div className="absolute inset-0 bg-cyan-300 rounded-full animate-progress-pulse"></div>
              )}

              {/* Main progress bar */}
              <div
                className={`relative bg-cyan-600 h-3 rounded-full transition-all duration-500 ease-out ${
                  isProcessing ? 'animate-bar-grow' : ''
                }`}
                style={{ width: `${Math.max(progress, isProcessing && progress === 0 ? 5 : 0)}%` }}
              >
                {/* Shimmer effect on the bar itself */}
                {isProcessing && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-40 animate-shimmer"></div>
                )}
              </div>
            </div>
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-600">{pendingCount}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{processingCount}</div>
              <div className="text-xs text-blue-600">Processing</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{completedCount}</div>
              <div className="text-xs text-green-600">Completed</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{errorCount}</div>
              <div className="text-xs text-red-600">Failed</div>
            </div>
          </div>

          {/* Download/Processing Section - MOVED TO TOP */}
          {allCompleted && completedCount > 0 && (
            <div className={`mb-6 bg-cyan-50 border-2 rounded-lg p-4 ${isDownloading ? 'border-cyan-400 animate-download-pulse' : 'border-cyan-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {isDownloading ? (
                    <svg className="w-6 h-6 text-cyan-600 mr-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-cyan-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  )}
                  <div>
                    <div className="font-semibold text-cyan-900 text-base">
                      {isDownloading ? (
                        completedCount === 1 ? 'Preparing download...' : 'Compressing files into ZIP...'
                      ) : (
                        'Ready to download!'
                      )}
                    </div>
                    <div className="text-sm text-cyan-700">
                      {completedCount === 1
                        ? 'Your project has been exported'
                        : `${completedCount} ${completedCount > 1 ? 'projects' : 'project'} exported successfully`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {hasError && errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-red-800">{errorMessage}</div>
              </div>
            </div>
          )}

          {/* Jobs List */}
          <div className="space-y-2">
            <div className="text-sm font-semibold text-gray-700 mb-3">Export Details</div>
            {jobs.map((job) => (
              <div
                key={job.jobId}
                className={`border rounded-lg p-4 ${
                  job.status === JOB_STATUS.COMPLETED
                    ? 'border-green-200 bg-green-50'
                    : job.status === JOB_STATUS.ERROR
                    ? 'border-red-200 bg-red-50'
                    : job.status === JOB_STATUS.PROCESSING
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    {getStatusIcon(job.status)}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{job.projectName || job.templateName || job.projectId}</div>
                      <div className="text-sm text-gray-500">
                        Format: {job.format?.toUpperCase() || 'Unknown'}
                      </div>
                      {job.error && (
                        <div className="text-sm text-red-600 mt-1">Error: {job.error}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {job.status === JOB_STATUS.COMPLETED && (job.downloadUrl || (job.childJobs && job.childJobs.length > 0)) && (
                      <button
                        onClick={() => handleIndividualDownload(job)}
                        className="group relative p-2 rounded-lg hover:bg-green-100 transition-colors"
                        title="Download"
                      >
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Download Manually
                        </span>
                      </button>
                    )}
                    {job.status === JOB_STATUS.ERROR && (
                      <Button
                        onClick={() => handleRetry(job)}
                        type="outlined"
                        className="ml-2"
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Warning during processing */}
          {!allCompleted && !hasError && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Please wait</p>
                  <p>Do not close this window while exports are processing. This may take a few minutes.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-between gap-3">
          {!canClose && (
            <Button type="outlined" onClick={handleCancel}>
              Cancel Export
            </Button>
          )}
          {canClose ? (
            <Button onClick={handleClose} className="ml-auto">
              Close
            </Button>
          ) : (
            <Button disabled className="opacity-50 cursor-not-allowed ml-auto">
              Processing... Please wait
            </Button>
          )}
        </div>
        </div>
      </div>
    </>
  );
};

export default ExportProgressModal;
